import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Banknote, Building2, Download, FileText, Receipt, TrendingUp, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ChartCard, DataGrid, KpiCard, MetricStrip } from "@/components/dashboard";
import { formatCurrency, formatShortMonth } from "@/lib/format";
import type {
  SalesByApartmentRow, SalesByClientRow, SalesInvoiceRow, SalesLineRow, SalesReportResponse,
} from "@shared/reports-sales";
import { exportSalesPdf } from "./sales-pdf";

export type SalesTabProps = {
  salesData?: SalesReportResponse;
  isLoading?: boolean;
};

const CHART_COLORS = { gross: "#6366f1", paid: "#22c55e" };

function statusVariant(status: string): "default" | "secondary" | "outline" | "destructive" {
  if (status === "paid") return "default";
  if (status === "pending" || status === "sent" || status === "issued" || status === "approved") return "outline";
  if (status === "cancelled" || status === "refunded") return "destructive";
  return "secondary";
}

function money(v: unknown): string {
  return formatCurrency(Number(v ?? 0));
}

function shortDate(iso: string): string {
  const [y = 1970, m = 1, d = 1] = iso.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function SectionCard({ icon: Icon, title, children }: { icon: typeof Users; title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          <Icon className="h-4 w-4 text-muted-foreground" />
          {title}
        </CardTitle>
        <Separator className="mt-3" />
      </CardHeader>
      <CardContent className="px-0 pb-2">{children}</CardContent>
    </Card>
  );
}

function TotalsFooter({ items }: { items: Array<{ label: string; value: string; tone?: "green" | "orange" }> }) {
  return (
    <div className="px-4 pt-2 pb-3 border-t flex flex-wrap gap-x-6 gap-y-1 text-xs">
      {items.map(it => (
        <span
          key={it.label}
          className={`font-medium ${it.tone === "green" ? "text-green-600 dark:text-green-400" : it.tone === "orange" ? "text-orange-600 dark:text-orange-400" : ""}`}
        >
          {it.label}: {it.value}
        </span>
      ))}
    </div>
  );
}

function useSalesColumns(): {
  client: ColumnDef<SalesByClientRow, unknown>[];
  apartment: ColumnDef<SalesByApartmentRow, unknown>[];
  line: ColumnDef<SalesLineRow, unknown>[];
  invoice: ColumnDef<SalesInvoiceRow, unknown>[];
} {
  const right = (v: unknown) => <span className="block text-right font-medium">{money(v)}</span>;
  const paidCell = (v: unknown) => <span className="block text-right text-green-600 dark:text-green-400">{money(v)}</span>;
  const pendingCell = (v: unknown) => <span className="block text-right text-orange-600 dark:text-orange-400">{money(v)}</span>;

  return useMemo(() => ({
    client: [
      { accessorKey: "clientName", header: "Client", cell: info => <span className="font-medium">{String(info.getValue())}</span> },
      { accessorKey: "apartments", header: "Apartments" },
      { accessorKey: "jobs", header: "Jobs" },
      { accessorKey: "lineItems", header: "Line items" },
      { accessorKey: "gross", header: "Gross", cell: info => right(info.getValue()) },
      { accessorKey: "paid", header: "Paid", cell: info => paidCell(info.getValue()) },
      { accessorKey: "pending", header: "Pending", cell: info => pendingCell(info.getValue()) },
    ],
    apartment: [
      { accessorKey: "apartmentName", header: "Apartment", cell: info => <span className="font-medium">{String(info.getValue())}</span> },
      { accessorKey: "clientName", header: "Client" },
      { accessorKey: "jobs", header: "Jobs" },
      { accessorKey: "lineItems", header: "Line items" },
      { accessorKey: "gross", header: "Gross", cell: info => right(info.getValue()) },
      { accessorKey: "paid", header: "Paid", cell: info => paidCell(info.getValue()) },
      { accessorKey: "pending", header: "Pending", cell: info => pendingCell(info.getValue()) },
    ],
    line: [
      { accessorKey: "date", header: "Date", cell: info => shortDate(String(info.getValue())) },
      { accessorKey: "typeLabel", header: "Type" },
      { accessorKey: "apartmentName", header: "Apartment" },
      { accessorKey: "clientName", header: "Client" },
      { accessorKey: "description", header: "Description", cell: info => <span className="text-muted-foreground line-clamp-1">{String(info.getValue() ?? "")}</span> },
      { accessorKey: "amount", header: "Amount", cell: info => right(info.getValue()) },
      { accessorKey: "status", header: "Status", cell: info => <Badge variant={statusVariant(String(info.getValue()))}>{String(info.getValue())}</Badge> },
      { accessorKey: "paymentMethod", header: "Method", cell: info => String(info.getValue() ?? "-") },
    ],
    invoice: [
      { accessorKey: "invoiceNumber", header: "Invoice", cell: info => <span className="font-medium">{String(info.getValue())}</span> },
      { accessorKey: "clientName", header: "Client" },
      { accessorKey: "issuedOn", header: "Issued", cell: info => shortDate(String(info.getValue())) },
      { accessorFn: row => `${shortDate(row.periodStart)} – ${shortDate(row.periodEnd)}`, id: "period", header: "Service period" },
      { accessorKey: "status", header: "Status", cell: info => <Badge variant={statusVariant(String(info.getValue()))}>{String(info.getValue())}</Badge> },
      { accessorKey: "total", header: "Total", cell: info => right(info.getValue()) },
      { accessorKey: "paid", header: "Paid", cell: info => paidCell(info.getValue()) },
      { accessorKey: "outstanding", header: "Outstanding", cell: info => pendingCell(info.getValue()) },
    ],
  }), []);
}

function SalesKpis({ summary, invoicesApplicable }: { summary: SalesReportResponse["summary"]; invoicesApplicable: boolean }) {
  const collectedPct = summary.grossSales > 0 ? Math.round((summary.paid / summary.grossSales) * 100) : 0;
  const invoiceHelper = invoicesApplicable
    ? `${summary.invoicedCount} invoices · ${money(summary.outstanding)} outstanding`
    : "not available per apartment";
  return (
    <MetricStrip cols={4}>
      <KpiCard label="Gross Sales" value={money(summary.grossSales)} helper={`${summary.lineItems} line items · ${summary.jobs} jobs`} icon={TrendingUp} variant="success" testId="stat-sales-gross" />
      <KpiCard label="Paid" value={money(summary.paid)} helper={`${collectedPct}% of gross`} icon={Banknote} variant="info" testId="stat-sales-paid" />
      <KpiCard label="Pending" value={money(summary.pending)} helper="not yet paid" icon={Receipt} variant="warning" testId="stat-sales-pending" />
      <KpiCard label="Invoiced" value={invoicesApplicable ? money(summary.invoicedTotal) : "—"} helper={invoiceHelper} icon={FileText} variant="default" testId="stat-sales-invoiced" />
    </MetricStrip>
  );
}

function SalesCharts({ report }: { report: SalesReportResponse }) {
  const topClients = useMemo(
    () => report.byClient.slice(0, 10).map(c => ({ name: c.clientName, gross: c.gross, paid: c.paid })),
    [report.byClient],
  );
  const monthly = useMemo(
    () => report.byMonth.map(m => ({ name: formatShortMonth(m.month), gross: m.gross, paid: m.paid })),
    [report.byMonth],
  );
  const tooltipStyle = { fontSize: 12, borderRadius: 8 };
  const tickMoney = (v: number) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v.toFixed(0)}`;

  return (
    <div className={`grid gap-4 ${monthly.length > 1 ? "lg:grid-cols-2" : ""}`}>
      <ChartCard title="Top clients" description="Gross vs paid, top 10 by gross sales" height={Math.max(200, topClients.length * 28)} isEmpty={topClients.length === 0} emptyText="No sales in period" testId="card-sales-clients">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={topClients} layout="vertical" margin={{ top: 4, right: 16, bottom: 0, left: 8 }} barCategoryGap={6}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={tickMoney} />
            <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 10 }} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => formatCurrency(v)} />
            <Bar dataKey="gross" name="Gross" fill={CHART_COLORS.gross} radius={[0, 4, 4, 0]} />
            <Bar dataKey="paid" name="Paid" fill={CHART_COLORS.paid} radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
      {monthly.length > 1 && (
        <ChartCard title="Sales by month" description="Gross vs paid per month in the selected range" height={240} testId="card-sales-monthly">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthly} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} width={48} tickFormatter={tickMoney} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v: number) => formatCurrency(v)} />
              <Bar dataKey="gross" name="Gross" fill={CHART_COLORS.gross} radius={[4, 4, 0, 0]} />
              <Bar dataKey="paid" name="Paid" fill={CHART_COLORS.paid} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}
    </div>
  );
}

export function SalesTab({ salesData, isLoading }: SalesTabProps) {
  const { data: orgInfo } = useQuery<{ id: number; name: string } | null>({ queryKey: ["/api/admin/org-info"] });
  const columns = useSalesColumns();

  if (!salesData) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          {isLoading ? "Building sales report…" : "Select a date range to build the sales report"}
        </CardContent>
      </Card>
    );
  }

  const { summary } = salesData;
  const gridProps = { initialDensity: "compact" as const, showDensityToggle: false, className: "border-0 shadow-none rounded-none" };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs text-muted-foreground">
          Sales are income records dated by service date in {salesData.period.timezone}, the same basis as invoices. Cancelled records are excluded.
        </p>
        <Button variant="outline" size="sm" onClick={() => exportSalesPdf(salesData, orgInfo?.name ?? "Staio")} data-testid="button-sales-pdf">
          <Download className="w-4 h-4 mr-2" /> Download PDF
        </Button>
      </div>

      <SalesKpis summary={summary} invoicesApplicable={salesData.invoices !== null} />
      <SalesCharts report={salesData} />

      <SectionCard icon={Users} title="Sales by client">
        <DataGrid data={salesData.byClient} columns={columns.client} searchPlaceholder="Search client…" exportFilename={`sales-by-client-${salesData.period.start}-${salesData.period.end}`} getRowId={r => String(r.clientId ?? "none")} emptyText="No sales in period" {...gridProps} />
        {salesData.byClient.length > 0 && (
          <TotalsFooter items={[
            { label: `${summary.clients} clients`, value: money(summary.grossSales) },
            { label: "Paid", value: money(summary.paid), tone: "green" },
            { label: "Pending", value: money(summary.pending), tone: "orange" },
          ]} />
        )}
      </SectionCard>

      <SectionCard icon={Building2} title="Sales by apartment">
        <DataGrid data={salesData.byApartment} columns={columns.apartment} searchPlaceholder="Search apartment or client…" exportFilename={`sales-by-apartment-${salesData.period.start}-${salesData.period.end}`} getRowId={r => String(r.apartmentId ?? "none")} emptyText="No sales in period" {...gridProps} />
        {salesData.byApartment.length > 0 && (
          <TotalsFooter items={[{ label: `${summary.apartments} apartments`, value: money(summary.grossSales) }]} />
        )}
      </SectionCard>

      {salesData.invoices !== null && (
      <SectionCard icon={FileText} title="Invoices issued in period">
        <DataGrid data={salesData.invoices} columns={columns.invoice} searchPlaceholder="Search invoice or client…" exportFilename={`invoices-${salesData.period.start}-${salesData.period.end}`} getRowId={r => String(r.id)} emptyText="No invoices issued in period" {...gridProps} />
        {salesData.invoices.length > 0 && (
          <TotalsFooter items={[
            { label: `${summary.invoicedCount} invoices`, value: money(summary.invoicedTotal) },
            { label: "Collected", value: money(summary.collected), tone: "green" },
            { label: "Outstanding", value: money(summary.outstanding), tone: "orange" },
          ]} />
        )}
      </SectionCard>
      )}

      <SectionCard icon={Receipt} title="Sales detail">
        <DataGrid data={salesData.lines} columns={columns.line} searchPlaceholder="Search apartment, client or description…" exportFilename={`sales-detail-${salesData.period.start}-${salesData.period.end}`} getRowId={r => String(r.id)} emptyText="No sales in period" {...gridProps} />
        {salesData.lines.length > 0 && (
          <TotalsFooter items={[{ label: `${summary.lineItems} line items`, value: money(summary.grossSales) }]} />
        )}
      </SectionCard>
    </div>
  );
}

export default SalesTab;
